/* eslint-disable @typescript-eslint/no-unsafe-return */
import { graphql, useStaticQuery } from 'gatsby';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import groq from 'groq';
import { ALL_LANGS, DEFAULT_LANG, SITE_BASE_URL } from '../constants';
import { LocationContext } from '../contexts/LocationContext';
import { getLocalizedUrlPath } from '../utils/i18n';
import { LocalizedSEO, LocalizedSEOWebsite } from '../graphql-fragments/seo';
import { usePreviewData } from '../preview/previewUtils';
import { getPageDocumentUrl, localize } from '../utils/sanity';
import {
  BusinessInfo,
  SocialLinks,
  buildBreadcrumbListSchema,
  buildOrganizationSchema,
  buildWebSiteSchema,
} from '../utils/structuredData';
import { urlJoin } from '../utils/utils';
import SEO from './SEO';

function addOgImageParams(url: string | undefined): string | undefined {
  if (!url) return undefined;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}w=1200&h=630&fit=crop&fm=jpg&q=85`;
}

interface LocalizedQueryData {
  site: {
    siteMetadata: {
      author: string;
    };
  };
  sanityWebsite: {
    seo: LocalizedSEOWebsite;
    legalName?: string | null;
    telephone?: string | null;
    contactEmail?: string | null;
    streetAddress?: string | null;
    addressLocality?: string | null;
    addressRegion?: string | null;
    postalCode?: string | null;
    addressCountry?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    openingHours?: (string | null)[] | null;
    logo?: { asset?: { url: string } } | null;
    footer?: {
      socialMediaBlock?: {
        facebookUrl?: string | null;
        instagramUrl?: string | null;
        whatsappUrl?: string | null;
        linkedinUrl?: string | null;
      } | null;
    } | null;
  };
}

interface PageSEOProps {
  defaultTitle?: string;
  defaultDescription?: string;
  defaultImageUrl?: string;
  pageSEO?: LocalizedSEO;
  structuredData?: Record<string, unknown>[];
}

function PageSEO({
  defaultTitle,
  defaultDescription,
  defaultImageUrl,
  pageSEO,
  structuredData: pageStructuredData,
}: PageSEOProps): React.ReactElement {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const location = useContext(LocationContext)!;
  const { i18n } = useTranslation();

  const websiteSeoStaticData = useStaticQuery(graphql`
    {
      site {
        siteMetadata {
          author
        }
      }
      sanityWebsite {
        seo {
          ...SEOWebsite
        }
        legalName
        telephone
        contactEmail
        streetAddress
        addressLocality
        addressRegion
        postalCode
        addressCountry
        latitude
        longitude
        openingHours
        logo {
          asset {
            url
          }
        }
        footer {
          socialMediaBlock {
            facebookUrl
            instagramUrl
            whatsappUrl
            linkedinUrl
          }
        }
      }
    }
  `);

  const groqQuery = groq`{
    "sanityWebsite": *[_type == "website"][0] {
      seo {
        ...
      },
      legalName,
      telephone,
      contactEmail,
      streetAddress,
      addressLocality,
      addressRegion,
      postalCode,
      addressCountry,
      latitude,
      longitude,
      openingHours,
      logo { asset-> { url } },
      footer {
        socialMediaBlock {
          facebookUrl,
          instagramUrl,
          whatsappUrl,
          linkedinUrl
        }
      }
    }
  }`;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const websiteSeoData = usePreviewData<any>(websiteSeoStaticData, {
    groqQuery,
    preprocessPreviewData: previewData => ({
      ...previewData,
      site: websiteSeoStaticData.site,
    }),
  });

  if (!websiteSeoData) {
    return <></>;
  }

  const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
  const localizedData = localize(websiteSeoData, langs) as LocalizedQueryData;
  const {
    site: {
      siteMetadata: { author },
    },
    sanityWebsite,
  } = localizedData;
  const websiteSEO = sanityWebsite.seo;
  const pageTitle = pageSEO?.title || defaultTitle;
  const pageShareDescription = pageSEO?.shareDescription || pageSEO?.description;
  const websiteTitle = websiteSEO.title;
  const websiteShareTitle = websiteSEO.shareTitle || websiteTitle;
  const websiteShareDescription = websiteSEO.shareDescription || websiteSEO.description;

  // Build hreflang links for all supported languages
  // Strip the current language prefix from the pathname to get the base path
  const currentLang = i18n.language;
  const basePath =
    currentLang !== DEFAULT_LANG && location.pathname.startsWith('/' + currentLang)
      ? location.pathname.slice(currentLang.length + 1) || '/'
      : location.pathname;
  const hreflangLinks =
    ALL_LANGS.length > 1
      ? [
          ...ALL_LANGS.map(lang => ({
            lang,
            url: urlJoin(SITE_BASE_URL, getLocalizedUrlPath(basePath, lang)),
          })),
          { lang: 'x-default', url: urlJoin(SITE_BASE_URL, basePath) },
        ]
      : undefined;

  const businessInfo: BusinessInfo = {
    legalName: sanityWebsite.legalName,
    telephone: sanityWebsite.telephone,
    contactEmail: sanityWebsite.contactEmail,
    streetAddress: sanityWebsite.streetAddress,
    addressLocality: sanityWebsite.addressLocality,
    addressRegion: sanityWebsite.addressRegion,
    postalCode: sanityWebsite.postalCode,
    addressCountry: sanityWebsite.addressCountry,
    latitude: sanityWebsite.latitude,
    longitude: sanityWebsite.longitude,
    openingHours: sanityWebsite.openingHours,
  };
  const social: SocialLinks = sanityWebsite.footer?.socialMediaBlock || {};
  const siteUrl = urlJoin(SITE_BASE_URL, '/');
  const isHomepage =
    location.pathname === '/' ||
    (currentLang !== DEFAULT_LANG && location.pathname === '/' + currentLang + '/');

  const structuredData: Record<string, unknown>[] = [
    buildOrganizationSchema({
      name: websiteTitle,
      url: siteUrl,
      logoUrl: sanityWebsite.logo?.asset?.url,
      description: websiteSEO.description,
      businessInfo,
      social,
    }),
  ];
  if (isHomepage) {
    structuredData.push(
      buildWebSiteSchema({
        name: websiteTitle,
        url: siteUrl,
        description: websiteSEO.description,
      }),
    );
  } else {
    const breadcrumbs = buildBreadcrumbListSchema({
      pathname: basePath,
      currentPageTitle: pageTitle,
    });
    if (breadcrumbs) structuredData.push(breadcrumbs);
  }
  if (pageStructuredData) structuredData.push(...pageStructuredData);

  return (
    <SEO
      title={pageTitle ? pageTitle + ' - ' + websiteTitle : websiteTitle}
      shareTitle={
        pageSEO?.shareTitle ||
        (pageTitle ? pageTitle + ' - ' + websiteShareTitle : websiteShareTitle)
      }
      description={pageSEO?.description || defaultDescription || websiteSEO.description}
      shareDescription={pageShareDescription || websiteShareDescription}
      url={urlJoin(SITE_BASE_URL, location.pathname)}
      canonicalUrl={
        pageSEO?.canonicalPage
          ? urlJoin(SITE_BASE_URL, getPageDocumentUrl(pageSEO.canonicalPage))
          : urlJoin(SITE_BASE_URL, location.pathname)
      }
      imageUrl={addOgImageParams(
        pageSEO?.image?.asset?.url || defaultImageUrl || websiteSEO.image?.asset?.url,
      )}
      lang={i18n.language}
      author={author}
      noFollow={pageSEO?.noFollow}
      noIndex={pageSEO?.noIndex}
      hreflangLinks={hreflangLinks}
      structuredData={structuredData}
    />
  );
}

export default PageSEO;
